import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import { Icon, IconType } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { MaybeText } from '@euler/components/MaybeText';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo, ReactNode } from 'react';
import {
  ActivityIndicator,
  StyleProp,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

export const Cell: FC<{
  label: ReactNode;
  labelDecoration?: ReactNode;
  required?: boolean;
  helpText?: ReactNode;
  helpTextIconHidden?: boolean;
  helpTextIcon?: IconType | 'spinner';
  helpTextIconSize?: number;
  helpTextIconColor?: string;
  helpTextStyle?: StyleProp<TextStyle>;
  style?: StyleProp<ViewStyle>;
  children?: ReactNode;
}> = memo(
  ({
    label,
    labelDecoration,
    helpText,
    helpTextIcon,
    helpTextIconHidden,
    helpTextIconColor,
    helpTextIconSize,
    helpTextStyle,
    required,
    children,
    style,
  }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    return (
      <View
        css={`
          padding: 0;
          margin: 0 ${rpx(10)}px;
        `}
        style={style}
      >
        <HStack justifyContent="flex-start">
          {label ? (
            <MaybeText
              text={label}
              css={`
                font-family: ${FontFamily.NotoSans.Medium};
                font-size: ${rpx(14)}px;
                line-height: ${rpx(18)}px;
                color: ${theme.order.labelColor};
                margin-bottom: ${rpx(5)}px;
              `}
            />
          ) : null}
          {labelDecoration ? (
            <MaybeText
              text={labelDecoration}
              css={`
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(12)}px;
                line-height: ${rpx(18)}px;
                color: ${theme.order.labelColor};
                margin-bottom: ${rpx(5)}px;
                margin-left: ${rpx(5)}px;
                opacity: 0.85;
              `}
            />
          ) : null}
          {required ? (
            <Typography.Label
              color={StatusColors.Danger}
              css={`
                margin-left: ${rpx(4)}px;
                font-size: ${rpx(16)}px;
                line-height: ${rpx(18)}px;
                transform: translateY(${rpx(2)}px);
              `}
            >
              *
            </Typography.Label>
          ) : null}
        </HStack>
        {children}
        {helpText ? (
          <HStack
            justifyContent="flex-start"
            css={`
              margin-top: ${rpx(5)}px;
            `}
          >
            {helpTextIcon === 'spinner' ? (
              <ActivityIndicator
                size="small"
                color={helpTextIconColor ?? theme.order.helpTextColor}
                css={`
                  transform: scale(${rpx(0.6)}) translateX(${rpx(-4)}px);
                  margin-right: ${rpx(-4)}px;
                `}
              />
            ) : helpTextIconHidden ? null : (
              <Icon
                name={helpTextIcon ?? 'information-outline'}
                size={helpTextIconSize ?? rpx(12)}
                color={helpTextIconColor ?? theme.order.helpTextColor}
                css={`
                  margin-right: ${rpx(3)}px;
                `}
              />
            )}
            <MaybeText
              text={helpText}
              css={`
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(12)}px;
                line-height: ${rpx(16)}px;
                color: ${theme.order.helpTextColor};
              `}
              style={helpTextStyle}
            />
          </HStack>
        ) : null}
      </View>
    );
  },
);
